import ky from "ky"
import { useEffect, useRef, useState } from "react"
import { FileText, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@f2e/ui/lib/utils"

import { getResourceUrl } from "@/lib/utils"
import { useToast, useGetAttachmentMarkdownApi } from "@/hooks"
import { useSysConfig } from "@/hooks"
import { FileAttachmentType } from "@/types"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"

export interface BaseFileBadgeProps {
  name: string
  type?: string
  url?: string
  index: number
  onRemove?: (index: number) => void
  showFileName?: boolean
  height?: string
  iconSize?: "sm" | "md" | "lg"
  attachmentId?: string
  attachmentType?: string
}

const MIN_SPINNER_MS = 2000
const MAX_DOWNLOAD_SIZE = 50 * 1024 * 1024

const BaseFileBadge = ({
  name,
  type,
  url,
  index,
  onRemove,
  showFileName = true,
  height = "h-10",
  iconSize = "md",
  attachmentId,
  attachmentType,
}: BaseFileBadgeProps) => {
  const config = useSysConfig()
  const abortRef = useRef<AbortController | null>(null)

  // Get icon size based on the prop
  const getIconDimension = () => {
    switch (iconSize) {
      case "sm":
        return { img: "w-4 h-4", icon: "h-4 w-4" }
      case "lg":
        return { img: "w-44 h-44", icon: "h-6 w-6" }
      case "md":
      default:
        return { img: "w-6 h-6", icon: "h-5 w-5" }
    }
  }

  const iconDim = getIconDimension()
  const isImage = type?.startsWith("image/") && url
  const [downloading, setDownloading] = useState(false)
  const { successToast, alertToast } = useToast()
  const { t } = useTranslation()
  // API hook for getting markdown content
  const getMarkdownApi = useGetAttachmentMarkdownApi(attachmentId || "")

  const handleDownload = async () => {
    if (downloading) return

    // Handle context_object attachments differently
    if (attachmentType === FileAttachmentType.ContextObject && attachmentId) {
      setDownloading(true)
      const downloadToast = successToast({
        description: t("history.downloading", { name }),
      })

      try {
        await getMarkdownApi.fetchFn({
          callbackFn: (data) => {
            if (data?.data) {
              const { content, name: fileName } = data.data

              if (!content) {
                console.error("No content received from API")
                alertToast({ description: t("history.downloadFailed") })
                return
              }

              // Create a safe filename by removing/replacing problematic characters
              const safeFileName = (fileName || name)
                .replace(/[^\w\s.-]/g, "") // Remove special characters except word chars, spaces, dots, hyphens
                .replace(/\s+/g, "_") // Replace spaces with underscores
                .substring(0, 100) // Limit length

              // Create a blob with the markdown content
              const blob = new Blob([content], {
                type: "text/markdown;charset=utf-8",
              })
              const blobUrl = window.URL.createObjectURL(blob)
              const a = document.createElement("a")
              a.style.display = "none"
              a.href = blobUrl
              a.download = `${safeFileName}.md`
              document.body.appendChild(a)
              a.click()
              window.URL.revokeObjectURL(blobUrl)
              document.body.removeChild(a)
            } else {
              console.error("Invalid response structure:", data)
              alertToast({ description: t("history.downloadFailed") })
            }
          },
        })
      } catch (error) {
        console.error("Failed to download markdown content:", error)
        alertToast({
          ...getUserFacingApiErrorToastProps(error, t, {
            title: t("history.downloadFailed"),
            fallbackDescription: t("history.downloadFailed"),
          }),
        })
      }

      setDownloading(false)
      downloadToast.dismiss()
      return
    }

    // Handle regular file downloads
    if (!url) return
    const resolvedUrl = getResourceUrl(url, config.serverRoot)
    setDownloading(true)
    const downloadToast = successToast({
      description: t("history.downloading", { name }),
    })
    const minDelay = new Promise((r) => setTimeout(r, MIN_SPINNER_MS))
    const doDownload = async () => {
      try {
        const controller = new AbortController()
        abortRef.current = controller
        const response = await ky.get(resolvedUrl, {
          timeout: 15000,
          signal: controller.signal,
        })
        const lenHeader = response.headers.get("content-length")
        const headerSize = lenHeader ? parseInt(lenHeader, 10) : 0
        if (
          headerSize &&
          !Number.isNaN(headerSize) &&
          headerSize > MAX_DOWNLOAD_SIZE
        ) {
          throw new Error("FILE_TOO_LARGE")
        }
        const blob = await response.blob()
        if (!headerSize && blob.size > MAX_DOWNLOAD_SIZE) {
          throw new Error("FILE_TOO_LARGE")
        }

        const blobUrl = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.style.display = "none"
        a.href = blobUrl
        a.download = name
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(blobUrl)
        document.body.removeChild(a)
      } catch (error) {
        const err = error as Error & { name?: string }
        if (err.name === "TimeoutError") {
          alertToast({ description: t("history.downloadTimeout") })
          return
        }
        if (err.message === "FILE_TOO_LARGE") {
          alertToast({ description: t("history.downloadTooLarge") })
          return
        }
        alertToast({
          ...getUserFacingApiErrorToastProps(error, t, {
            title: t("history.downloadFailed"),
            fallbackDescription: t("history.downloadFailed"),
          }),
        })
        return
      }
    }
    try {
      await Promise.all([doDownload(), minDelay])
    } finally {
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
      if (downloadToast && typeof downloadToast.dismiss === "function") {
        downloadToast.dismiss()
      }
      setDownloading(false)
    }
  }

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
    }
  }, [])

  return (
    <div
      className={cn(
        "flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700",
        "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200",
        downloading ? "cursor-default opacity-90" : "cursor-pointer",
        height
      )}
      role="button"
      aria-busy={downloading}
      aria-disabled={downloading}
      tabIndex={0}
      onClick={handleDownload}
      onKeyDown={(e) => {
        if (downloading) return
        if (e.key === "Enter" || e.key === " ") handleDownload()
      }}
    >
      {/* File icon or image preview */}
      <div className="relative shrink-0">
        {isImage ? (
          <div className={cn("rounded overflow-hidden", iconDim.img)}>
            <img
              src={getResourceUrl(url!, config.serverRoot)}
              alt={name}
              className="w-full h-full object-cover"
              onError={() => {}}
              onLoad={() => {}}
            />
          </div>
        ) : (
          <FileText
            className={cn(iconDim.icon, "text-gray-600 dark:text-gray-400")}
          />
        )}
        {downloading && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <Loader2 className="h-4 w-4 text-white animate-spin" />
          </div>
        )}
      </div>

      {/* File name - only show for non-images or when explicitly requested for images */}
      {(!isImage || showFileName) && (
        <span className="truncate max-w-[150px] grow">{name}</span>
      )}

      {/* Remove button */}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove(index)
          }}
          className="h-5 w-5 p-0 shrink-0 cursor-pointer text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          aria-label={`Remove ${name}`}
        >
          ×
        </button>
      )}
    </div>
  )
}

export default BaseFileBadge
